import { CommonModule, NgIf } from '@angular/common'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { ActivatedRoute, Router } from '@angular/router'
import { CloudinaryUploadService } from '@app/services/cloudinary-upload.service'
import { AuthService } from '@auth/services/auth.service'
import { WorkspaceRoleService } from '@auth/services/workspace-role.service'
import { NbButtonModule, NbCardModule } from '@nebular/theme'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '@workspaces/services/workspace.service'
import { format, parse } from 'date-fns'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CreateComponent } from '../create/create.component'
import { PlanBadgeComponent } from '../plan-badge/plan-badge.component'

/**
 * Component that represents a workspace
 */
@Component({
  selector: 'ngx-workspaces-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  imports: [MatIconModule, NbCardModule, NbButtonModule, PlanBadgeComponent, NgIf, CreateComponent],
  standalone: true
})
export class DetailsComponent implements OnInit, OnDestroy {
  /**
   * Subject that stops all hot observables
   */
  public _destroy$ = new Subject()
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * No available actions mode
   */
  @Input()
  noActions = false

  /**
   * Workspace model
   */
  @Input()
  workspace: Workspace

  /**
   * Workspace change event emitter
   */
  @Output()
  workspaceChange: EventEmitter<Workspace> = new EventEmitter<Workspace>()

  /**
   * Workspace id
   */
  @Input()
  workspaceId: number

  /**
   * Display mode
   */
  @Input()
  mode: 'informative' | 'regular' = 'informative'

  /**
   * Show plan
   */
  @Input()
  showPlan = false

  /**
   * Emit workspace events
   */
  @Input()
  emitEvents = true

  /**
   * Represents a workspace
   */
  form: UntypedFormGroup

  /**
   * Performing async operation
   */
  processing = false

  /**
   * Organizer mode
   */
  organizer = true

  /**
   * Enterprise mode
   */
  enterprise = false

  /**
   * Editing?
   */
  editing = false

  /**
   * Display fragment to scroll to
   */
  fragment

  /**
   * Default workspace logos
   */
  defaultLogo = 'https://res.cloudinary.com/contactless/image/upload/v1598337942/public/default-event-logo.svg'

  /**
   * Had error fetching the current workspace logo?
   */
  logoHasError = false

  /**
   * Cloudinary upload widget reference
   */
  cloudinaryUploadWidget
  /**
   * Creates the component and injects it's dependencies
   * @param authService AuthService
   * @param router Router
   * @param route ActivatedRoute
   * @param workspaceService WorkspaceService
   * @param cus CloudinaryUploadService
   */
  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public workspaceService: WorkspaceService,
    private cus: CloudinaryUploadService,
    public workspaceRoleService: WorkspaceRoleService
  ) {}
  /**
   * Intializes the component
   */
  ngOnInit(): void {
    this.route.fragment.pipe(takeUntil(this._destroy$)).subscribe((fragment) => {
      this.fragment = fragment
      const selector = '#' + this.fragment
      if (fragment) {
        document.querySelector(selector)?.scrollIntoView()
      }
    })
  }

  /**
   * Toggles editing mode
   */
  toggleEditing(event?) {
    this.authService.currentWorkspace = this.workspace
    this.authService.currentParentWorkspace = this.workspace.parent ? (this.workspace.parent_workspace as Workspace) : this.workspace
    if (event) {
      event.stopPropagation()
    }
    this.editing = !this.editing
  }

  /**
   * Updates current workspace
   */
  updateWorkspace(value) {
    this.workspace = value
    this.toggleEditing()
  }

  /**
   * Formats workspace creation date
   */
  createdAt() {
    return this.workspace.created_at ? format(parse(this.workspace.created_at, 'yyyy-MM-dd HH:mm:ss', Date.now()), 'dd-MMM-yyyy') : ''
  }

  /**
   * Formats workspace update date
   */
  updatedAt() {
    return this.workspace.last_use_diff_to_human ? format(parse(this.workspace.last_use_diff_to_human, 'yyyy-MM-dd HH:mm:ss', Date.now()), 'dd-MMM-yyyy') : ''
  }

  /**
   * Gets workspace role
   */
  get role() {
    return this.workspace?.parent && this.workspace.parent_workspace?.id ? 'Sub Entity' : 'Main Entity'
  }

  /**
   * Marks the workspace as last-active on the server, then navigates to events
   * (or org-settings billing for accountants).
   */
  navigateEvents() {
    if (!this.emitEvents) {
      return
    }
    this.authService
      .switchActiveOrganization$(this.workspace)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.workspaceRoleService.navigateToWorkspaceLanding()
      })
  }
  /**
   * Scrolls to certain fragment on the document
   * @param fragment string
   * @param event MouseEvent
   */
  scrollToParent(fragment, event) {
    event.stopPropagation()

    if (fragment.slug) {
      this.router.navigateByUrl(`/workspaces/list#${fragment.slug}`)
    }
  }

  /**
   * Is the current user an admin?
   */
  get isAdmin(): boolean {
    return this.authService.role === 'Admin'
  }

  /**
   * Returns workspace logo, based on several factors and optimizes it using cloudinary
   */
  get logo() {
    return (this.logoHasError ? this.defaultLogo : this.workspace.logo ? this.workspace.logo : this.defaultLogo).replace(
      '/upload/',
      '/upload/b_rgb:ffffff,bo_1px_solid_rgb:ffffff,c_pad,f_png,h_77,r_3,w_77/'
    )
  }

  /**
   * Opens the cloudinary upload widget
   * @param event MouseEvent
   */
  uploadLogo(event) {
    event.stopPropagation()
    if (!this.cloudinaryUploadWidget) {
      this.cus.bootDetails(this)
    }
    if (!this.cloudinaryUploadWidget.isShowing()) {
      this.cloudinaryUploadWidget.open()
    }
  }
}
